import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Upload, FileText, CheckCircle, Loader2, LogOut, User, Building,
  Calendar, DollarSign, MapPin, Clock, AlertCircle, Pen, Send, Download,
  Menu, LayoutDashboard, FolderOpen, FileSignature, CreditCard, Briefcase,
  Phone, Mail, Plus, Eye, History, Target
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEAD_STATUSES = [
  'New Lead', 'Contacted', 'Meeting Scheduled', 'Proposal Sent', 'Negotiation', 'Converted', 'Lost'
];

const STATUS_COLORS = {
  'New Lead': 'bg-blue-100 text-blue-700',
  'Contacted': 'bg-purple-100 text-purple-700',
  'Meeting Scheduled': 'bg-yellow-100 text-yellow-700',
  'Proposal Sent': 'bg-cyan-100 text-cyan-700',
  'Negotiation': 'bg-orange-100 text-orange-700',
  'Converted': 'bg-green-100 text-green-700',
  'Lost': 'bg-red-100 text-red-700'
};

const ACTIVITY_TYPES = [
  { value: 'call', label: 'Phone Call' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'email', label: 'Email Sent' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'status_change', label: 'Status Update' },
  { value: 'comment', label: 'General Comment' }
];

const PRODUCTS_LIST = [
  'STEAM-IE Kits',
  'LMS Platform',
  'Training Programs',
  'CodeBunny Platform',
  'Innovation Labs',
  'Customized Learning'
];

const EmployeePortal = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState(null);

  // Daily Work / Sales States
  const [myLeads, setMyLeads] = useState([]);
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showLeadDetailModal, setShowLeadDetailModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadHistory, setLeadHistory] = useState([]);
  const [newLeadForm, setNewLeadForm] = useState({
    school_name: '',
    address: '',
    city: '',
    state: '',
    poc_name: '',
    poc_designation: '',
    poc_phone: '',
    poc_email: '',
    school_type: 'Private',
    num_students: '',
    expected_deal_value: '',
    products_interested: [],
    comments: '',
    follow_up_date: ''
  });
  const [activityForm, setActivityForm] = useState({
    activity_type: 'comment',
    description: '',
    new_status: '',
    follow_up_date: ''
  });

  const [bankDetails, setBankDetails] = useState({
    account_holder_name: '',
    account_number: '',
    confirm_account_number: '',
    ifsc_code: '',
    bank_name: '',
    branch_name: ''
  });

  useEffect(() => {
    const role = localStorage.getItem('userRole');
    const employeeId = localStorage.getItem('employeeId');
    
    if (role !== 'employee' || !employeeId) {
      navigate('/employee/login');
      return;
    }
    
    fetchProfile(employeeId);
    fetchMyLeads(employeeId);
  }, [navigate]);

  const fetchMyLeads = async (employeeId) => {
    try {
      const response = await fetch(`${API_URL}/api/sales/leads/my?employee_id=${employeeId}`);
      const data = await response.json();
      if (data.success) {
        setMyLeads(data.leads);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const fetchProfile = async (employeeId) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/employee/${employeeId}/profile`);
      const data = await response.json();
      if (data.success) {
        setEmployee(data.employee);
        if (data.employee.documents?.bank_details) {
          setBankDetails({
            ...data.employee.documents.bank_details,
            confirm_account_number: data.employee.documents.bank_details.account_number
          });
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('employeeToken');
    localStorage.removeItem('employeeId');
    localStorage.removeItem('userRole');
    navigate('/employee/login');
  };

  const handleFileUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingDoc(docType);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', docType);

    try {
      const response = await fetch(`${API_URL}/api/employee/${employee.id}/upload-document`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        alert(`${docType.replace('_', ' ')} uploaded successfully!`);
        fetchProfile(employee.id);
      } else {
        alert(data.detail || 'Error uploading document');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error uploading document');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleBankDetailsSubmit = async (e) => {
    e.preventDefault();
    if (bankDetails.account_number !== bankDetails.confirm_account_number) {
      alert('Account numbers do not match!');
      return;
    }
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/employee/${employee.id}/bank-details`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bankDetails)
      });
      const data = await response.json();
      if (data.success) {
        alert('Bank details saved successfully!');
        fetchProfile(employee.id);
      } else {
        alert(data.detail || 'Error saving bank details');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error saving bank details');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitDocuments = async () => {
    if (!window.confirm('Submit all documents for verification? You cannot make changes after submission.')) return;
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/employee/${employee.id}/submit-documents`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        alert('Documents submitted for verification!');
        fetchProfile(employee.id);
      } else {
        alert(data.detail || 'Error submitting documents');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error submitting documents');
    } finally {
      setIsSaving(false);
    }
  };

  // ==================== DAILY WORK / SALES FUNCTIONS ====================

  const handleCreateLead = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/sales/leads?employee_id=${employee.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newLeadForm,
          num_students: parseInt(newLeadForm.num_students) || 0,
          expected_deal_value: parseFloat(newLeadForm.expected_deal_value) || 0,
          status: 'New Lead'
        })
      });
      const data = await response.json();
      if (data.success) {
        alert('Lead created successfully!');
        setShowNewLeadModal(false);
        setNewLeadForm({
          school_name: '', address: '', city: '', state: '',
          poc_name: '', poc_designation: '', poc_phone: '', poc_email: '',
          school_type: 'Private', num_students: '', expected_deal_value: '',
          products_interested: [], comments: '', follow_up_date: ''
        });
        fetchMyLeads(employee.id);
      } else {
        alert(data.detail || 'Error creating lead');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error creating lead');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!selectedLead) return;
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/sales/leads/${selectedLead.id}/activity?employee_id=${employee.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(activityForm)
      });
      const data = await response.json();
      if (data.success) {
        alert('Activity added successfully!');
        setShowActivityModal(false);
        setActivityForm({ activity_type: 'comment', description: '', new_status: '', follow_up_date: '' });
        fetchMyLeads(employee.id);
        if (showLeadDetailModal) {
          fetchLeadHistory(selectedLead.id);
        }
      } else {
        alert(data.detail || 'Error adding activity');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error adding activity');
    } finally {
      setIsSaving(false);
    }
  };

  const fetchLeadHistory = async (leadId) => {
    try {
      const response = await fetch(`${API_URL}/api/sales/leads/${leadId}`);
      const data = await response.json();
      if (data.success) {
        setSelectedLead(data.lead);
        setLeadHistory(data.activities);
        setShowLeadDetailModal(true);
      }
    } catch (error) {
      console.error('Error fetching lead history:', error);
    }
  };

  const toggleProductInterest = (product) => {
    setNewLeadForm(prev => ({
      ...prev,
      products_interested: prev.products_interested.includes(product)
        ? prev.products_interested.filter(p => p !== product)
        : [...prev.products_interested, product]
    }));
  };

  // ==================== END DAILY WORK FUNCTIONS ====================

  // Signature Canvas Functions
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  };

  useEffect(() => {
    if (employee?.status === 'agreement_sent' && activeTab === 'agreement') {
      setTimeout(initCanvas, 100);
    }
  }, [employee?.status, activeTab]);

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureData(canvas.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    initCanvas();
    setSignatureData(null);
  };

  const handleSignAgreement = async () => {
    if (!signatureData) {
      alert('Please sign the agreement first');
      return;
    }
    if (!window.confirm('Submit your signed agreement?')) return;
    
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/employee/${employee.id}/sign-agreement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_data: signatureData })
      });
      const data = await response.json();
      if (data.success) {
        alert('Agreement signed successfully! Waiting for admin approval.');
        fetchProfile(employee.id);
      } else {
        alert(data.detail || 'Error signing agreement');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error signing agreement');
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusStep = (status) => {
    const steps = ['offer_accepted', 'documents_uploaded', 'documents_verified', 'agreement_sent', 'agreement_signed', 'active'];
    return steps.indexOf(status) + 1;
  };

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'dailywork', label: 'Daily Work', icon: Briefcase },
    { id: 'documents', label: 'Documents', icon: FolderOpen },
    { id: 'bank', label: 'Bank Details', icon: CreditCard },
    { id: 'agreement', label: 'Agreement', icon: FileSignature },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100" data-testid="loading-spinner">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p>Error loading profile. Please login again.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 transform transition-transform duration-300 ease-in-out ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:inset-auto`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-white font-bold text-lg">Employee Portal</h1>
                <p className="text-slate-400 text-xs">STEAM-IE</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {sidebarItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setMobileSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === item.id 
                    ? 'bg-blue-600 text-white' 
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 mb-4 px-2">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                {employee.name?.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{employee.name}</p>
                <p className="text-slate-400 text-xs">{employee.position}</p>
              </div>
            </div>
            <Button onClick={handleLogout} variant="outline" className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <button onClick={() => setMobileSidebarOpen(true)} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg">
                <Menu className="w-6 h-6" />
              </button>
              <h2 className="text-xl font-bold text-gray-900 capitalize">{activeTab}</h2>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              employee.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
            }`}>
              {employee.status === 'active' ? 'Active Employee' : 'Onboarding'}
            </div>
          </div>
        </header>

        {/* Progress Bar */}
        <div className="bg-white border-b px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between max-w-3xl">
            {['Accepted', 'Docs Uploaded', 'Verified', 'Agreement', 'Signed', 'Active'].map((step, index) => (
              <div key={step} className="flex flex-col items-center flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                  getStatusStep(employee.status) > index 
                    ? 'bg-green-500 text-white' 
                    : getStatusStep(employee.status) === index + 1
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}>
                  {getStatusStep(employee.status) > index ? <CheckCircle className="w-4 h-4" /> : index + 1}
                </div>
                <p className="text-[10px] mt-1 text-center text-gray-500 hidden sm:block">{step}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="p-4 lg:p-8">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Profile Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Profile Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center text-white text-3xl font-bold">
                      {employee.name?.charAt(0)}
                    </div>
                    <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Full Name</p>
                        <p className="font-medium">{employee.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium">{employee.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Position</p>
                        <p className="font-medium">{employee.position}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Department</p>
                        <p className="font-medium">{employee.department}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Location</p>
                        <p className="font-medium">{employee.location}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Joining Date</p>
                        <p className="font-medium">{employee.date_of_joining}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">CTC</p>
                        <p className="font-medium text-green-600">₹{employee.ctc?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Reports To</p>
                        <p className="font-medium">{employee.reports_to}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Status Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Onboarding Status</CardTitle>
                </CardHeader>
                <CardContent>
                  {employee.status === 'offer_accepted' && (
                    <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
                      <FolderOpen className="w-10 h-10 text-blue-600" />
                      <div>
                        <p className="font-medium text-blue-900">Upload Required Documents</p>
                        <p className="text-sm text-blue-700">Please upload your documents and bank details to proceed</p>
                      </div>
                    </div>
                  )}
                  {employee.status === 'documents_uploaded' && (
                    <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-lg">
                      <Clock className="w-10 h-10 text-purple-600" />
                      <div>
                        <p className="font-medium text-purple-900">Documents Under Review</p>
                        <p className="text-sm text-purple-700">HR is verifying your documents. You will be notified once done.</p>
                      </div>
                    </div>
                  )}
                  {employee.status === 'documents_verified' && (
                    <div className="flex items-center gap-4 p-4 bg-indigo-50 rounded-lg">
                      <CheckCircle className="w-10 h-10 text-indigo-600" />
                      <div>
                        <p className="font-medium text-indigo-900">Documents Verified</p>
                        <p className="text-sm text-indigo-700">HR will send your employment agreement shortly.</p>
                      </div>
                    </div>
                  )}
                  {employee.status === 'agreement_sent' && (
                    <div className="flex items-center gap-4 p-4 bg-cyan-50 rounded-lg">
                      <FileSignature className="w-10 h-10 text-cyan-600" />
                      <div>
                        <p className="font-medium text-cyan-900">Sign Your Agreement</p>
                        <p className="text-sm text-cyan-700">Go to Agreement tab to review and sign your employment agreement.</p>
                      </div>
                    </div>
                  )}
                  {employee.status === 'agreement_signed' && (
                    <div className="flex items-center gap-4 p-4 bg-teal-50 rounded-lg">
                      <Clock className="w-10 h-10 text-teal-600" />
                      <div>
                        <p className="font-medium text-teal-900">Awaiting Approval</p>
                        <p className="text-sm text-teal-700">Your signed agreement is being reviewed by HR.</p>
                      </div>
                    </div>
                  )}
                  {employee.status === 'active' && (
                    <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                      <div>
                        <p className="font-medium text-green-900">Welcome to STEAM-IE!</p>
                        <p className="text-sm text-green-700">Your onboarding is complete. You are now an active employee.</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Daily Work Tab */}
          {activeTab === 'dailywork' && (
            <div className="space-y-6">
              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
                  <CardContent className="p-4">
                    <Building className="w-8 h-8 mb-2 opacity-80" />
                    <p className="text-2xl font-bold">{myLeads.length}</p>
                    <p className="text-blue-100 text-sm">Total Leads</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-yellow-500 to-orange-500 text-white border-0">
                  <CardContent className="p-4">
                    <Clock className="w-8 h-8 mb-2 opacity-80" />
                    <p className="text-2xl font-bold">{myLeads.filter(l => !['Converted', 'Lost'].includes(l.status)).length}</p>
                    <p className="text-yellow-100 text-sm">Active Leads</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
                  <CardContent className="p-4">
                    <Target className="w-8 h-8 mb-2 opacity-80" />
                    <p className="text-2xl font-bold">{myLeads.filter(l => l.status === 'Converted').length}</p>
                    <p className="text-green-100 text-sm">Converted</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
                  <CardContent className="p-4">
                    <DollarSign className="w-8 h-8 mb-2 opacity-80" />
                    <p className="text-2xl font-bold">₹{myLeads.filter(l => l.status === 'Converted').reduce((sum, l) => sum + (l.expected_deal_value || 0), 0).toLocaleString()}</p>
                    <p className="text-purple-100 text-sm">Converted Value</p>
                  </CardContent>
                </Card>
              </div>

              {/* Add New Lead Button */}
              <div className="flex justify-end">
                <Button onClick={() => setShowNewLeadModal(true)} className="bg-orange-600 hover:bg-orange-700">
                  <Plus className="w-4 h-4 mr-2" /> Add New Lead
                </Button>
              </div>

              {/* My Leads Table */}
              <Card>
                <CardHeader>
                  <CardTitle>My Leads</CardTitle>
                  <CardDescription>Schools assigned to you for follow-up</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="py-3 px-2">School</th>
                          <th className="py-3 px-2">City</th>
                          <th className="py-3 px-2">POC</th>
                          <th className="py-3 px-2">Status</th>
                          <th className="py-3 px-2">Follow-up</th>
                          <th className="py-3 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {myLeads.map((lead) => (
                          <tr key={lead.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-2">
                              <p className="font-medium">{lead.school_name}</p>
                              <p className="text-xs text-gray-500">{lead.school_type}</p>
                            </td>
                            <td className="py-3 px-2">{lead.city}</td>
                            <td className="py-3 px-2">
                              <p>{lead.poc_name}</p>
                              <p className="text-xs text-gray-500">{lead.poc_phone}</p>
                            </td>
                            <td className="py-3 px-2">
                              <Badge className={STATUS_COLORS[lead.status]}>{lead.status}</Badge>
                            </td>
                            <td className="py-3 px-2">
                              {lead.follow_up_date || 'Not set'}
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => fetchLeadHistory(lead.id)} title="View Details">
                                  <Eye className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => { setSelectedLead(lead); setShowActivityModal(true); }} title="Add Update">
                                  <Plus className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {myLeads.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Building className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p>No leads assigned yet</p>
                        <Button variant="link" onClick={() => setShowNewLeadModal(true)}>Add your first lead</Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <Card>
              <CardHeader>
                <CardTitle>Upload Documents</CardTitle>
                <CardDescription>Upload required documents for verification</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Aadhar Card */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium">Aadhar Card *</p>
                      <p className="text-sm text-gray-500">Upload front and back</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {employee.documents?.aadhar ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" /> Uploaded
                      </span>
                    ) : (
                      <Label htmlFor="aadhar" className="cursor-pointer">
                        <div className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                          {uploadingDoc === 'aadhar' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                          Upload
                        </div>
                        <Input id="aadhar" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => handleFileUpload(e, 'aadhar')} disabled={employee.status !== 'offer_accepted'} />
                      </Label>
                    )}
                  </div>
                </div>

                {/* PAN Card */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium">PAN Card *</p>
                      <p className="text-sm text-gray-500">Upload PAN card copy</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {employee.documents?.pan ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" /> Uploaded
                      </span>
                    ) : (
                      <Label htmlFor="pan" className="cursor-pointer">
                        <div className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                          {uploadingDoc === 'pan' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                          Upload
                        </div>
                        <Input id="pan" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => handleFileUpload(e, 'pan')} disabled={employee.status !== 'offer_accepted'} />
                      </Label>
                    )}
                  </div>
                </div>

                {/* Relieving Letter */}
                {!employee.is_fresher && (
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium">Relieving Letter *</p>
                        <p className="text-sm text-gray-500">From previous employer</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {employee.documents?.relieving_letter ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" /> Uploaded
                        </span>
                      ) : (
                        <Label htmlFor="relieving_letter" className="cursor-pointer">
                          <div className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                            {uploadingDoc === 'relieving_letter' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                            Upload
                          </div>
                          <Input id="relieving_letter" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => handleFileUpload(e, 'relieving_letter')} disabled={employee.status !== 'offer_accepted'} />
                        </Label>
                      )}
                    </div>
                  </div>
                )}

                {employee.is_fresher && (
                  <div className="p-4 bg-blue-50 rounded-lg flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600" />
                    <p className="text-sm text-blue-700">As a fresher, you don&apos;t need to upload relieving letter or salary slips.</p>
                  </div>
                )}

                {/* Submit Button */}
                {employee.status === 'offer_accepted' && (
                  <Button 
                    className="w-full mt-6 bg-blue-600 hover:bg-blue-700" 
                    onClick={handleSubmitDocuments}
                    disabled={isSaving || !employee.documents?.aadhar || !employee.documents?.pan || !employee.documents?.bank_details || (!employee.is_fresher && !employee.documents?.relieving_letter)}
                    data-testid="submit-documents-btn"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Submit Documents for Verification
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Bank Details Tab */}
          {activeTab === 'bank' && (
            <Card>
              <CardHeader>
                <CardTitle>Bank Account Details</CardTitle>
                <CardDescription>For salary disbursement</CardDescription>
              </CardHeader>
              <CardContent>
                {employee.documents?.bank_details ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 rounded-lg flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <p className="text-green-700">Bank details saved successfully</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Account Holder</p>
                        <p className="font-medium">{employee.documents.bank_details.account_holder_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Account Number</p>
                        <p className="font-medium">{employee.documents.bank_details.account_number}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">IFSC Code</p>
                        <p className="font-medium">{employee.documents.bank_details.ifsc_code}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Bank Name</p>
                        <p className="font-medium">{employee.documents.bank_details.bank_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Branch</p>
                        <p className="font-medium">{employee.documents.bank_details.branch_name}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleBankDetailsSubmit} className="space-y-4" data-testid="bank-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label>Account Holder Name *</Label>
                        <Input 
                          value={bankDetails.account_holder_name} 
                          onChange={(e) => setBankDetails({...bankDetails, account_holder_name: e.target.value})} 
                          required 
                          disabled={employee.status !== 'offer_accepted'}
                        />
                      </div>
                      <div>
                        <Label>Account Number *</Label>
                        <Input 
                          value={bankDetails.account_number} 
                          onChange={(e) => setBankDetails({...bankDetails, account_number: e.target.value})} 
                          required 
                          disabled={employee.status !== 'offer_accepted'}
                        />
                      </div>
                      <div>
                        <Label>Confirm Account Number *</Label>
                        <Input 
                          value={bankDetails.confirm_account_number} 
                          onChange={(e) => setBankDetails({...bankDetails, confirm_account_number: e.target.value})} 
                          required 
                          disabled={employee.status !== 'offer_accepted'}
                        />
                      </div>
                      <div>
                        <Label>IFSC Code *</Label>
                        <Input 
                          value={bankDetails.ifsc_code} 
                          onChange={(e) => setBankDetails({...bankDetails, ifsc_code: e.target.value.toUpperCase()})} 
                          required 
                          disabled={employee.status !== 'offer_accepted'}
                        />
                      </div>
                      <div>
                        <Label>Bank Name *</Label>
                        <Input 
                          value={bankDetails.bank_name} 
                          onChange={(e) => setBankDetails({...bankDetails, bank_name: e.target.value})} 
                          required 
                          disabled={employee.status !== 'offer_accepted'}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Branch Name *</Label>
                        <Input 
                          value={bankDetails.branch_name} 
                          onChange={(e) => setBankDetails({...bankDetails, branch_name: e.target.value})} 
                          required 
                          disabled={employee.status !== 'offer_accepted'}
                        />
                      </div>
                    </div>
                    {employee.status === 'offer_accepted' && (
                      <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isSaving}>
                        {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                        Save Bank Details
                      </Button>
                    )}
                  </form>
                )}
              </CardContent>
            </Card>
          )}

          {/* Agreement Tab */}
          {activeTab === 'agreement' && (
            <Card>
              <CardHeader>
                <CardTitle>Employment Agreement</CardTitle>
              </CardHeader>
              <CardContent>
                {employee.status === 'agreement_sent' ? (
                  <div className="space-y-6">
                    {/* Agreement Download */}
                    {employee.agreement_pdf_url && (
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileText className="w-6 h-6 text-blue-600" />
                            <div>
                              <p className="font-medium">Employment Agreement</p>
                              <p className="text-sm text-gray-500">Please review before signing</p>
                            </div>
                          </div>
                          <a href={`${API_URL}${employee.agreement_pdf_url}`} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline">
                              <Download className="w-4 h-4 mr-2" /> View PDF
                            </Button>
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Signature Pad */}
                    <div className="border rounded-lg p-4">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Pen className="w-4 h-4" /> Digital Signature
                      </h4>
                      <p className="text-sm text-gray-500 mb-4">Draw your signature below to sign the employment agreement</p>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-white">
                        <canvas
                          ref={canvasRef}
                          width={500}
                          height={150}
                          className="w-full touch-none cursor-crosshair"
                          onMouseDown={startDrawing}
                          onMouseMove={draw}
                          onMouseUp={stopDrawing}
                          onMouseLeave={stopDrawing}
                          onTouchStart={startDrawing}
                          onTouchMove={draw}
                          onTouchEnd={stopDrawing}
                        />
                      </div>
                      <div className="flex gap-3 mt-4">
                        <Button variant="outline" onClick={clearSignature}>
                          Clear
                        </Button>
                        <Button 
                          className="flex-1 bg-blue-600 hover:bg-blue-700" 
                          onClick={handleSignAgreement} 
                          disabled={!signatureData || isSaving}
                        >
                          {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Pen className="w-4 h-4 mr-2" />}
                          Sign & Submit Agreement
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : employee.status === 'agreement_signed' || employee.status === 'active' ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Agreement Signed</h3>
                    <p className="text-gray-500 mb-4">Your employment agreement has been signed.</p>
                    {employee.signed_agreement_url && (
                      <a href={`${API_URL}${employee.signed_agreement_url}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline">
                          <Download className="w-4 h-4 mr-2" /> Download Signed Agreement
                        </Button>
                      </a>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Agreement Not Available Yet</h3>
                    <p className="text-gray-500">
                      {employee.status === 'offer_accepted' && 'Please upload and submit your documents first.'}
                      {employee.status === 'documents_uploaded' && 'Your documents are being verified.'}
                      {employee.status === 'documents_verified' && 'HR will send your agreement shortly.'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* New Lead Modal */}
      <Dialog open={showNewLeadModal} onOpenChange={setShowNewLeadModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
            <DialogDescription>Record a new school lead you are visiting</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateLead} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>School Name *</Label>
                <Input 
                  value={newLeadForm.school_name} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, school_name: e.target.value})}
                  required 
                  placeholder="Enter school name"
                />
              </div>
              <div className="col-span-2">
                <Label>Address *</Label>
                <Input 
                  value={newLeadForm.address} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, address: e.target.value})}
                  required 
                  placeholder="Full address"
                />
              </div>
              <div>
                <Label>City *</Label>
                <Input 
                  value={newLeadForm.city} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, city: e.target.value})}
                  required 
                  placeholder="City"
                />
              </div>
              <div>
                <Label>State *</Label>
                <Input 
                  value={newLeadForm.state} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, state: e.target.value})}
                  required 
                  placeholder="State"
                />
              </div>
              <div>
                <Label>POC Name *</Label>
                <Input 
                  value={newLeadForm.poc_name} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, poc_name: e.target.value})}
                  required 
                  placeholder="Point of Contact name"
                />
              </div>
              <div>
                <Label>POC Designation *</Label>
                <Input 
                  value={newLeadForm.poc_designation} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, poc_designation: e.target.value})}
                  required 
                  placeholder="e.g., Principal, Director"
                />
              </div>
              <div>
                <Label>POC Phone *</Label>
                <Input 
                  value={newLeadForm.poc_phone} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, poc_phone: e.target.value})}
                  required 
                  placeholder="Phone number"
                />
              </div>
              <div>
                <Label>POC Email</Label>
                <Input 
                  type="email"
                  value={newLeadForm.poc_email} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, poc_email: e.target.value})}
                  placeholder="Email address"
                />
              </div>
              <div>
                <Label>School Type *</Label>
                <Select value={newLeadForm.school_type} onValueChange={(v) => setNewLeadForm({...newLeadForm, school_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Private">Private</SelectItem>
                    <SelectItem value="Government">Government</SelectItem>
                    <SelectItem value="Trust">Trust/NGO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Number of Students</Label>
                <Input 
                  type="number"
                  value={newLeadForm.num_students} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, num_students: e.target.value})}
                  placeholder="Approx. students"
                />
              </div>
              <div>
                <Label>Expected Deal Value (₹)</Label>
                <Input 
                  type="number"
                  value={newLeadForm.expected_deal_value} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, expected_deal_value: e.target.value})}
                  placeholder="Expected value"
                />
              </div>
              <div>
                <Label>Follow-up Date</Label>
                <Input 
                  type="date"
                  value={newLeadForm.follow_up_date} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, follow_up_date: e.target.value})}
                />
              </div>
              <div className="col-span-2">
                <Label>Products Interested In</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {PRODUCTS_LIST.map((product) => (
                    <Badge 
                      key={product}
                      variant={newLeadForm.products_interested.includes(product) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => toggleProductInterest(product)}
                    >
                      {product}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="col-span-2">
                <Label>Comments</Label>
                <Textarea 
                  value={newLeadForm.comments} 
                  onChange={(e) => setNewLeadForm({...newLeadForm, comments: e.target.value})}
                  placeholder="Initial comments or notes about the school..."
                  rows={3}
                />
              </div>
            </div>
            <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700" disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Lead
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Activity Modal */}
      <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Activity Update</DialogTitle>
            <DialogDescription>Record your activity for {selectedLead?.school_name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddActivity} className="space-y-4">
            <div>
              <Label>Activity Type *</Label>
              <Select value={activityForm.activity_type} onValueChange={(v) => setActivityForm({...activityForm, activity_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTIVITY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea 
                value={activityForm.description} 
                onChange={(e) => setActivityForm({...activityForm, description: e.target.value})}
                placeholder="Describe what happened..."
                rows={3}
                required
              />
            </div>
            <div>
              <Label>Update Status (Optional)</Label>
              <Select value={activityForm.new_status} onValueChange={(v) => setActivityForm({...activityForm, new_status: v})}>
                <SelectTrigger><SelectValue placeholder="Keep current status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Keep current status</SelectItem>
                  {LEAD_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Set Follow-up Date (Optional)</Label>
              <Input 
                type="date"
                value={activityForm.follow_up_date} 
                onChange={(e) => setActivityForm({...activityForm, follow_up_date: e.target.value})}
              />
            </div>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Activity
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Modal */}
      <Dialog open={showLeadDetailModal} onOpenChange={setShowLeadDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              {selectedLead?.school_name}
            </DialogTitle>
            <DialogDescription>Lead details and activity history</DialogDescription>
          </DialogHeader>
          
          {selectedLead && (
            <div className="space-y-6">
              {/* Lead Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span>{selectedLead.city}, {selectedLead.state}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span>{selectedLead.poc_phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-500" />
                  <span>{selectedLead.poc_name} ({selectedLead.poc_designation})</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span>{selectedLead.poc_email || 'N/A'}</span>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Current Status</p>
                  <Badge className={STATUS_COLORS[selectedLead.status]}>{selectedLead.status}</Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Expected Value</p>
                  <p className="font-medium">₹{(selectedLead.expected_deal_value || 0).toLocaleString()}</p>
                </div>
                <Button variant="outline" onClick={() => { setShowActivityModal(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> Add Update
                </Button>
              </div>

              {/* Activity History */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <History className="w-5 h-5" /> Activity History
                </h3>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {leadHistory.map((activity, idx) => (
                    <div key={idx} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex flex-col items-center">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        {idx < leadHistory.length - 1 && <div className="w-0.5 flex-1 bg-gray-300 mt-1"></div>}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm capitalize">{activity.activity_type.replace('_', ' ')}</p>
                          <span className="text-xs text-gray-500">
                            {new Date(activity.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                        {activity.new_status && (
                          <Badge className={`${STATUS_COLORS[activity.new_status]} mt-1`}>
                            Status → {activity.new_status}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                  {leadHistory.length === 0 && (
                    <p className="text-center text-gray-500 py-4">No activity recorded yet</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeePortal;
